import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import type { LauncherPreference } from './coachLauncherPosition'
import { coachLauncherPreferences } from './coachLauncherPreferences'

export type CoachDockKind = 'header' | 'compact-header' | 'menu' | 'game' | 'fallback'

type Placement = {
  host: HTMLElement | null
  hasHeader: boolean
  compact: boolean
  inMenu: boolean
  inGame: boolean
  inGameActions: boolean
  isPending: boolean
  positionRevision: number
  resetPosition: () => void
  readPosition: () => LauncherPreference | null
  writePosition: (position: LauncherPreference) => void
  register: (element: HTMLElement, kind: CoachDockKind, onResetPosition?: () => void) => () => void
  registerPending: () => () => void
}

const PlacementContext = createContext<Placement | null>(null)

export function CoachLauncherPlacement({ children }: { children: ReactNode }) {
  const [header, setHeader] = useState<{
    host: HTMLElement
    compact: boolean
    inMenu: boolean
  } | null>(null)
  const [fallback, setFallback] = useState<HTMLElement | null>(null)
  const [game, setGame] = useState<{
    host: HTMLElement
    resetPosition?: () => void
  } | null>(null)
  const gamePosition = useRef<LauncherPreference | null>(null)
  const [pending, setPending] = useState(0)
  const [positionRevision, setPositionRevision] = useState(0)
  // Game and fallback sessions never overwrite the user's menu preference.
  const sessionPlacement = !!game || (!header && !!fallback)
  const compact = sessionPlacement || !!header?.compact
  const inMenu = !sessionPlacement && !!header?.inMenu
  const readPosition = useCallback(
    () => (sessionPlacement ? gamePosition.current : coachLauncherPreferences.read()),
    [sessionPlacement],
  )
  const writePosition = useCallback(
    (position: LauncherPreference) => {
      if (sessionPlacement) gamePosition.current = position
      else coachLauncherPreferences.write(position)
    },
    [sessionPlacement],
  )
  const resetPosition = useCallback(() => {
    if (game?.resetPosition) game.resetPosition()
    else if (sessionPlacement) gamePosition.current = null
    else coachLauncherPreferences.clear()
    setPositionRevision(revision => revision + 1)
  }, [game, sessionPlacement])
  const registerPending = useCallback(() => {
    setPending(count => count + 1)
    return () => setPending(count => count - 1)
  }, [])
  const register = useCallback(
    (element: HTMLElement, kind: CoachDockKind, onResetPosition?: () => void) => {
      if (kind === 'header' || kind === 'compact-header' || kind === 'menu') {
        setHeader({ host: element, compact: kind === 'compact-header', inMenu: kind === 'menu' })
        return () => setHeader(current => (current?.host === element ? null : current))
      }
      gamePosition.current = null
      if (kind === 'fallback') {
        setFallback(element)
        return () => setFallback(current => (current === element ? null : current))
      }
      setGame({ host: element, resetPosition: onResetPosition })
      return () => setGame(current => (current?.host === element ? null : current))
    },
    [],
  )
  const value = useMemo(
    () => ({
      host: game?.host ?? header?.host ?? fallback,
      hasHeader: !!header || !!game,
      compact,
      inMenu,
      inGame: !!game,
      inGameActions: !!game?.resetPosition,
      isPending: pending > 0,
      positionRevision,
      resetPosition,
      readPosition,
      writePosition,
      register,
      registerPending,
    }),
    [
      header,
      fallback,
      game,
      compact,
      inMenu,
      pending,
      positionRevision,
      resetPosition,
      readPosition,
      writePosition,
      register,
      registerPending,
    ],
  )
  return <PlacementContext.Provider value={value}>{children}</PlacementContext.Provider>
}

export function useCoachLauncherPlacement() {
  return useContext(PlacementContext)
}
